import {CSSProperties, PropsWithChildren, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Alert,
    Box,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Dialog as MuiDialog,
    Stack,
    Typography,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/ReportGmailerrorred";
import {ActionButton} from "./ActionButton";
import {StepNavigation, StepNavigationProps} from "./StepNavigation";

export type Props = PropsWithChildren & DefaultProps & SubmitProps & DeleteProps & {wizzardProps?: WizzardProps};

interface DefaultProps {
    title: React.ReactNode;
    titleIcon: React.ReactNode;
    headerContent?: React.ReactNode;
    submitTitle?: string;
    cancelTitle?: string;
    deleteTitle?: string;
    error?: string;
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
    width?: string;
    height?: string;
    minHeight?: string;
    maxHeight?: string;
    maxWidth?: string;
    minWidth?: string;
    padding?: string;
    disableDefaultClose?: boolean;
    onReset?: () => void;
    enableReset?: boolean;
    shouldCloseOnSubmit?: boolean;
    isLoading?: boolean;
}

type SubmitProps =
    | {
          enableSubmit: true;
          onSubmit: () => void;
      }
    | {
          enableSubmit?: false;
          onSubmit?: () => void;
      };

type DeleteProps =
    | {
          enableDelete: true;
          deleteConfirmation: string;
          onDelete: () => void;
      }
    | {
          enableDelete?: false;
          deleteConfirmation?: string;
          onDelete?: () => void;
      };

type WizzardProps = StepNavigationProps | undefined;

export const Dialog: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
    const isCancelOnly = !props.enableSubmit && !props.enableDelete;
    //We want to have custom padding for a map
    const padding = props.padding ? props.padding : "1.5rem";

    return (
        <MuiDialog
            PaperProps={{
                sx: {
                    borderRadius: "6px",
                    minWidth: "630px",
                    ...(props.width && {width: props.width, maxWidth: props.width}),
                    ...(props.minWidth && {minWidth: props.minWidth}),
                    ...(props.maxWidth && {maxWidth: props.maxWidth}),
                    ...(props.height && {height: props.height, maxHeight: props.height}),
                    ...(props.minHeight && {minHeight: props.minHeight}),
                    ...(props.maxHeight && {maxHeight: props.maxHeight}),
                },
            }}
            sx={{
                backdropFilter: "blur(5px) sepia(5%)",
            }}
            open={props.open}
            onClose={(_, reason) => {
                if (props.disableDefaultClose) {
                    if (reason === "backdropClick") {
                        return;
                    }
                }
                props.onClose(false);
            }}
            disableRestoreFocus
        >
            <DialogTitle
                sx={{
                    height: "24px",
                    fontSize: "16px",
                    lineHeight: "normal",
                    textTransform: "uppercase",
                    verticalAlign: "middle",
                    fontWeight: "500",
                    bgcolor: "#F3F5FA",
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" gap="0.75rem" width="100%">
                        {props.titleIcon}
                        <Typography sx={{paddingTop: "0.1rem"}}>{props.title}</Typography>
                    </Stack>
                    {props.headerContent}
                </Stack>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{padding: 0}}
                style={{...disableOnTrue(deleteConfirm), ...dimOnTrue(deleteConfirm)}}
            >
                {props.error && (
                    <Alert severity="error" sx={{paddingX: "1.5rem"}}>
                        {props.error}
                    </Alert>
                )}
                {props.isLoading && (
                    <Stack
                        alignItems="center"
                        padding="1rem"
                        direction="row"
                        gap="1"
                        sx={{
                            backgroundColor: "#F3F5FA",
                            color: "#404db8",
                            margin: "1.5rem",
                            marginBottom: 0,
                            borderRadius: "4px",
                        }}
                    >
                        <CircularProgress size={24} />
                        <span style={{paddingLeft: "1.5rem"}}>{t("dialogs.loading")}</span>
                    </Stack>
                )}
                <Box padding={padding}>{props.children}</Box>
            </DialogContent>
            <DialogActions
                sx={{
                    height: "2rem",
                    fontSize: "16px",
                    textTransform: "uppercase",
                    verticalAlign: "middle",
                    fontWeight: "500",
                    bgcolor: "#F3F5FA",
                    paddingX: "1.5rem",
                    paddingY: "1.125rem",
                    justifyContent: "space-between",
                }}
            >
                <Stack direction="row" gap={1}>
                    {props.enableDelete && !deleteConfirm && (
                        <ActionButton id="delete" color="error" icon="delete" onClick={() => setDeleteConfirm(true)}>
                            {props.deleteTitle ?? "Delete..."}
                        </ActionButton>
                    )}
                    {props.enableReset && props.onReset && (
                        <ActionButton
                            id="reset"
                            color="secondary"
                            icon="none"
                            onClick={() => props.onReset && props.onReset()}
                        >
                            {t("dialogs.actions.clear")}
                        </ActionButton>
                    )}
                    {props.wizzardProps && (
                        <StepNavigation
                            currentStep={props.wizzardProps.currentStep}
                            steps={props.wizzardProps.steps}
                            stepsTitles={props.wizzardProps.stepsTitles}
                            prevStep={props.wizzardProps.prevStep}
                            nextStep={props.wizzardProps.nextStep}
                        />
                    )}
                    {deleteConfirm && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                textTransform: "none",
                            }}
                        >
                            <WarningIcon color="error" />
                            <Box sx={{ml: "0.75rem"}}>{props.deleteConfirmation}</Box>
                        </div>
                    )}
                </Stack>
                <div>
                    <ActionButton
                        id="cancel"
                        icon={isCancelOnly ? "none" : "back"}
                        color={isCancelOnly ? "primary" : "secondary"}
                        onClick={deleteConfirm ? () => setDeleteConfirm(false) : () => props.onClose(false)}
                        sx={{ml: "1rem"}}
                    >
                        {props.cancelTitle ?? t("dialogs.actions.cancel")}
                    </ActionButton>
                    {deleteConfirm && (
                        <ActionButton
                            id="deleteConfirm"
                            icon="delete"
                            color="error"
                            onClick={props.onDelete}
                            sx={{ml: "1rem"}}
                        >
                            {props.deleteTitle ?? t("dialogs.actions.delete")}
                        </ActionButton>
                    )}
                    {props.enableReset && (
                        <>
                            <ActionButton
                                id="submitClose"
                                icon="check"
                                color="primary"
                                onClick={() => {
                                    if (props.onSubmit) {
                                        props.onSubmit();
                                        if (props.shouldCloseOnSubmit) props.onClose(true);
                                    }
                                }}
                                size="small"
                                sx={{
                                    ml: "1rem",
                                }}
                            >
                                {t("dialogs.actions.submit_and_close")}
                            </ActionButton>
                        </>
                    )}
                    {!deleteConfirm && props.enableSubmit && (
                        <ActionButton
                            id="submit"
                            icon="check"
                            color="primary"
                            onClick={() => {
                                props.onSubmit();
                            }}
                            sx={{ml: "1rem"}}
                            disabled={props.isLoading}
                        >
                            {props.submitTitle ?? t("dialogs.actions.submit")}
                        </ActionButton>
                    )}
                </div>
            </DialogActions>
        </MuiDialog>
    );
};

const dimOnTrue = (flag: boolean): CSSProperties => {
    return {
        opacity: flag ? 0.5 : 1,
    };
};

const disableOnTrue = (flag: boolean): CSSProperties => {
    return {
        pointerEvents: flag ? "none" : "initial",
    };
};
